import { useState, useEffect } from 'react';
import axios from 'axios';
import { FaXmark } from 'react-icons/fa6';
import { FaSpinner } from 'react-icons/fa';

const BASE_URL = process.env.REACT_APP_BE_MAIN_URL;

/**
 * Displays a PDF/image inline inside the app, fetched through an authenticated
 * backend endpoint (never a raw S3 URL — the bucket is private, so a direct
 * link/redirect to S3 returns Access Denied outside the app).
 *
 * Pass exactly one of:
 *   - fileId: renders the original document via GET /ocr/file-pdf/<fileId>/
 *   - src: a ready-to-use backend URL (e.g. the annotated-drawing endpoint),
 *          fetched the same authenticated way.
 */
export default function PdfViewerModal({ open, onClose, fileId, src, title, contentType }) {
  const [objectUrl, setObjectUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;

    let currentUrl = null;
    let cancelled = false;
    setLoading(true);
    setError('');

    const url = src || (fileId ? `${BASE_URL}/ocr/file-pdf/${fileId}/` : null);
    if (!url) {
      setError('Nothing to display.');
      setLoading(false);
      return;
    }

    const fetchDoc = async () => {
      try {
        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('userToken')}` },
          responseType: 'blob',
        });
        if (cancelled) return;
        currentUrl = URL.createObjectURL(res.data);
        setObjectUrl(currentUrl);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || 'Failed to load document.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchDoc();

    return () => {
      cancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fileId, src]);

  if (!open) return null;

  const isImage = contentType === 'image';

  return (
    <div className="fixed inset-0 bg-black/70 z-[70] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full h-full max-w-5xl overflow-hidden flex flex-col">
        <div className="px-4 py-3 bg-[#1e5045] text-white flex items-center justify-between flex-shrink-0">
          <span className="font-medium truncate">{title || 'Document'}</span>
          <button onClick={onClose} className="hover:bg-white/10 rounded p-1.5 transition-colors">
            <FaXmark size={18} />
          </button>
        </div>

        <div className="flex-1 min-h-0 bg-gray-100 flex items-center justify-center">
          {loading && (
            <div className="flex flex-col items-center gap-3 text-gray-500">
              <FaSpinner className="animate-spin" size={28} />
              <span>Loading document...</span>
            </div>
          )}

          {!loading && error && (
            <div className="text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              {error}
            </div>
          )}

          {!loading && !error && objectUrl && (
            isImage ? (
              <img src={objectUrl} alt={title || 'Document'} className="max-w-full max-h-full object-contain" />
            ) : (
              <iframe src={objectUrl} title={title || 'Document'} className="w-full h-full border-0" />
            )
          )}
        </div>
      </div>
    </div>
  );
}
